/**
 * 저장 직전 불변식 (Amendment A0, A4, A5, A7). 순수 함수.
 * Repository는 모든 쓰기에서 이 함수를 호출하고, 위반이 하나라도 있으면 쓰기를 거부한다.
 * UI·서비스·AI import 어느 경로로도 우회할 수 없다.
 */
import type { Actor, AnyEntity } from '../schema/v1';

export type Violation = { code: string; field?: string };

/** CLIENT_REPORT 자동 근거 임계값 (SPEAKER_ROLE_PROTOCOL §8) */
export const CLIENT_REPORT_MIN_ROLE_CONFIDENCE = 0.85;

/** 메타 필드: 동결 entity에서도 바뀔 수 있는 값 */
const META_FIELDS = new Set(['revision', 'updatedAt', 'updatedBy', 'lastModifiedDevice', 'deletedAt']);

/** 동결 규칙: 승인된 기록·RAW 전사는 내용이 바뀌면 안 된다 */
export function isFrozen(e: AnyEntity): boolean {
  switch (e.entityType) {
    case 'CounselingJournal':
    case 'CaseFormulationRevision':
    case 'DiagnosticConsideration':
    case 'DocumentArtifact':
      return e.status === 'approved';
    case 'RiskAssessment':
      return e.counselorApproved;
    case 'GoalProgress':
      return e.approvedStatus !== undefined;
    case 'Transcript':
      return e.source === 'samsung_raw' || e.source === 'apple_raw';
    default:
      return false;
  }
}

/** 동결 entity에서 허용되는 1회성 연결 필드 */
function allowedOnFrozen(prev: AnyEntity, field: string): boolean {
  if (prev.entityType === 'CounselingJournal' && field === 'supersededById') {
    return prev.supersededById === undefined || prev.supersededById === null;
  }
  return false;
}

function stableJson(v: unknown): string {
  return JSON.stringify(v, (_k, val: unknown) =>
    val && typeof val === 'object' && !Array.isArray(val)
      ? Object.fromEntries(Object.entries(val as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)))
      : val,
  );
}

export function changedContentFields(prev: AnyEntity, next: AnyEntity): string[] {
  const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
  const p = prev as Record<string, unknown>;
  const n = next as Record<string, unknown>;
  return [...keys].filter((k) => !META_FIELDS.has(k) && stableJson(p[k]) !== stableJson(n[k]));
}

export function checkWrite(prev: AnyEntity | null, next: AnyEntity, actor: Actor): Violation[] {
  const v: Violation[] = [];

  // 기본 식별자 불변
  if (prev) {
    if (prev.id !== next.id || prev.entityType !== next.entityType) v.push({ code: 'identity-changed' });
    if (prev.createdAt !== next.createdAt || prev.createdBy !== next.createdBy)
      v.push({ code: 'creation-meta-changed' });
  }

  // 동결
  if (prev && isFrozen(prev)) {
    if (actor === 'ai-import') v.push({ code: 'ai-import-on-frozen' });
    for (const f of changedContentFields(prev, next)) {
      if (!allowedOnFrozen(prev, f)) v.push({ code: 'frozen-field-changed', field: f });
    }
  }

  // AI가 승인 상태를 직접 만들 수 없다
  if (actor === 'ai-import') {
    const approvedLike =
      ('status' in next && next.status === 'approved') ||
      (next.entityType === 'RiskAssessment' && next.counselorApproved) ||
      (next.entityType === 'GoalProgress' && next.approvedStatus !== undefined) ||
      (next.entityType === 'CaseFormulationRevision' && next.approvedByCounselor);
    if (approvedLike) v.push({ code: 'ai-cannot-approve' });
  }

  v.push(...evidenceRules(next, actor));
  v.push(...analysisRules(next, actor, prev ?? undefined));
  v.push(...diagnosticConsistency(next));
  return v;
}

function evidenceRules(e: AnyEntity, actor: Actor): Violation[] {
  if (e.entityType !== 'ClinicalEvidence') return [];
  const v: Violation[] = [];
  if ((e.source === 'ai-analysis' || actor === 'ai-import') && e.evidenceType === 'FACT') v.push({ code: 'ai-cannot-create-fact' });
  // 확인(verified)은 상담자만: AI가 가져온 진술도 상담자가 직접 확인하면 확인됨으로 바꿀 수 있다
  if (actor !== 'user' && e.verified) v.push({ code: 'ai-cannot-verify' });
  if (e.source === 'counselor-memo' && e.evidenceType !== 'COUNSELOR_OBSERVATION') {
    v.push({ code: 'memo-must-be-observation' });
  }
  if (e.evidenceType === 'FACT' && e.source !== 'external-record' && e.source !== 'user-entry') {
    v.push({ code: 'fact-source-invalid' });
  }
  if (e.evidenceType === 'CLIENT_REPORT' && (e.source === 'transcript' || e.source === 'ai-analysis')) {
    const strong =
      e.roleAtExtraction === 'client' &&
      (e.roleConfidenceAtExtraction ?? 0) >= CLIENT_REPORT_MIN_ROLE_CONFIDENCE &&
      e.agreementStrength !== 'weak-agreement';
    if (!strong && !e.needsVerification) v.push({ code: 'client-report-gate' });
    if (!strong && e.verified) v.push({ code: 'client-report-gate' });
  }
  return v;
}

/**
 * AI는 '확인됨'을 새로 만들 수 없다. 상담자가 이미 확인해 둔 항목이 들어 있는 기록(개정안 복제·분석 덧붙이기)은 그대로 둘 수 있다.
 */
function analysisRules(e: AnyEntity, actor: Actor, prev?: AnyEntity): Violation[] {
  if (actor !== 'ai-import') return [];
  const v: Violation[] = [];
  const itemsOf = (e: AnyEntity) => {
    switch (e.entityType) {
      case 'SessionAnalysis':
        return [
          ...(e.emotions ?? []),
          ...(e.cognitions ?? []),
          ...(e.behaviors ?? []),
          ...(e.relationshipPatterns ?? []),
        ];
      case 'CaseFormulationRevision':
        return [
          ...(e.coreEmotions ?? []),
          ...(e.coreCognitiveThemes ?? []),
          ...(e.beliefHypotheses ?? []),
          ...(e.behavioralPatterns ?? []),
          ...(e.relationalPatterns ?? []),
          ...(e.allianceFactors ?? []),
          ...(e.therapyInterferingPatterns ?? []),
        ];
      default:
        return [];
    }
  };
  const items = itemsOf(e);
  const wasConfirmed = new Set(prev ? itemsOf(prev).filter((i) => i.status === 'confirmed').map((i) => i.id) : []);
  if (items.some((i) => i.status === 'confirmed' && !wasConfirmed.has(i.id))) v.push({ code: 'ai-cannot-confirm' });
  if (e.entityType === 'DiagnosticConsideration') {
    // 상담자가 이미 외부 확인으로 넣어 둔 항목을 그대로 두는 것은 허용 (같은 id, 같은 상태·출처)
    const kept = new Set(
      prev?.entityType === 'DiagnosticConsideration'
        ? prev.considerations
            .filter((c) => c.status === 'externally_confirmed' || c.source === 'external-confirmation')
            .map((c) => `${c.id}|${c.status}|${c.source}`)
        : [],
    );
    if (
      e.considerations.some(
        (c) => (c.status === 'externally_confirmed' || c.source === 'external-confirmation') && !kept.has(`${c.id}|${c.status}|${c.source}`),
      )
    ) {
      v.push({ code: 'ai-cannot-externally-confirm' });
    }
  }
  return v;
}

/** 상담자 직접 입력 경로에서도 externally_confirmed는 source와 일치해야 한다 */
export function diagnosticConsistency(e: AnyEntity): Violation[] {
  if (e.entityType !== 'DiagnosticConsideration') return [];
  return e.considerations.some((c) => (c.status === 'externally_confirmed') !== (c.source === 'external-confirmation'))
    ? [{ code: 'external-confirmation-mismatch' }]
    : [];
}
